import React, { useEffect, useState } from "react";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { IUserReport } from "../../services/types/users.types";
import { useAddExercisesToUserMutation } from "../../services/api/user.api";
import { useAppSelector } from "../../services/hooks";
import { exercisesCategoriesSelector } from "../../services/selectors/utils.selectors";
import { getCategory } from "../../utils/utils";
import { TExercise } from "../../utils/constants";
import BackArrowButton from "../backArrowButton/backArrowButton";
import ExerciseSelectionModal from "../modals/exerciseSelectionModal/exerciseSelectionModal";
import Modal from "../modals/modal/Modal";
import ActionButtonTooltip from "../ActionButtonTooltip/actionButtonTooltip";
import { useGetExercisesMutation } from "../../services/api/utils.api";
import { useBulkUnassignExercisesMutation } from "../../services/api/admin";
import { adminSelector } from "../../services/selectors/user.selectors";
import styles from "./userExercisesTable.module.css";

interface UserExercisesTableProps {
  user: IUserReport;
  onClose: () => void;
}

function UserExercisesTable({ user, onClose }: UserExercisesTableProps) {
  const admin = useAppSelector(adminSelector);
  const exercisesCategories = useAppSelector(exercisesCategoriesSelector);
  
  const [getUserExercises, { data: userExercisesData, isLoading: isUserExercisesLoading }] = 
    useAddExercisesToUserMutation();
  const [getExercises] = useGetExercisesMutation();
  const [bulkUnassignExercises, { isLoading: isDeleting }] = useBulkUnassignExercisesMutation();
  
  const [userExercises, setUserExercises] = useState<TExercise[]>([]);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [allExercises, setAllExercises] = useState<TExercise[]>([]);
  const [hasLoadedInitially, setHasLoadedInitially] = useState(false);
  const [exerciseToDelete, setExerciseToDelete] = useState<TExercise | null>(null);

  // Load user exercises on component mount
  useEffect(() => {
    if (user?.Users?.Guid) {
      getUserExercises({
        type: 1, // GET user exercises
        usersGuid: user.Users.Guid,
      });
    }
  }, [user?.Users?.Guid, getUserExercises]);

  // Load all exercises for the attachment modal
  useEffect(() => {
    if (admin.companyId) {
      getExercises().then((response) => {
        if ('data' in response && response.data) {
          setAllExercises(response.data);
        }
      });
    }
  }, [admin.companyId, getExercises]);

  // Update user exercises when data is received
  useEffect(() => {
    if (userExercisesData !== undefined) {
      setUserExercises(userExercisesData);
      setHasLoadedInitially(true);
    }
  }, [userExercisesData]);

  const handleAttachExercises = () => {
    setShowAttachmentModal(true);
  };

  const handleModalClose = () => {
    setShowAttachmentModal(false);
    // Reload user exercises after modal closes
    reloadUserExercises();
  };

  const reloadUserExercises = () => {
    if (user?.Users?.Guid) {
      setHasLoadedInitially(false); // Reset loading state
      getUserExercises({
        type: 1,
        usersGuid: user.Users.Guid,
      });
    }
  };

  const handleDeleteExercise = async (exerciseId: number) => {
    try {
      if (user?.Users?.Id) {
        await bulkUnassignExercises({
          exerciseIds: [exerciseId],
          userIds: [user.Users.Id],
          companyId: admin.companyId
        }).unwrap();
        
        // Reload user exercises after deletion
        if (user?.Users?.Guid) {
          getUserExercises({
            type: 1,
            usersGuid: user.Users.Guid,
          });
        }
        
        setExerciseToDelete(null); // Clear the confirmation state
      }
    } catch (error) {
      console.error('Error removing exercise:', error);
      // You could add a toast notification here for error handling
    }
  };

  const handleDeleteConfirm = (exercise: TExercise) => {
    setExerciseToDelete(exercise);
  };

  const handleDeleteCancel = () => {
    setExerciseToDelete(null);
  };

  // Show loading state if we're loading OR haven't loaded initially yet
  if (isUserExercisesLoading || !hasLoadedInitially) {
    return (
      <div className="loading">
        <p>{t("LOADING_LABEL")}...</p>
      </div>
    );
  }

  return (
    <article className="menuSection">
      <BackArrowButton
        text={t("PREVIOUS_SCREEN")}
        onClick={onClose}
        position="right"
      />

      <div className={styles.header}>
        <h2 className={"title title_blue"}>{t("EXERCISES_TITLE")} - {user.Users.FirstName} {user.Users.LastName}</h2>
        <button 
        type={"button"}
              className="addBtn"
          // className="button hover"
          onClick={handleAttachExercises}
        >
          {t("ATTACH_EXERCISES")}
        </button>
      </div>

      {userExercises.length > 0 ? (
        <article className="section">
          <ul className="list">
            <li className="listRow listRow_header">
              <p className="listCell">{t('NUMBER_2')}</p>
              <p className="listCell">{t("TITLE")}</p>
              <p className="listCell">{t("RRM")}</p>
              <p className="listCell">{t("DESCRIPTION")}</p>
              <p className="listCell">{t("CATEGORY")}</p>
              <p className="listCell">{t("COLUMN_STATUS")}</p>
              <p className="listCell">{t("COLUMN_OPTIONS")}</p>
            </li>

            {userExercises.map((exercise, index) => (
              <li
                key={exercise.id}
                className={`listRow ${index % 2 === 0 && "listRow_colored"}`}
              >
                <p className="listCell">{exercise.id}</p>
                <p className="listCell">{exercise.title}</p>
                <p className="listCell">{exercise.generalRRMSEQ}</p>
                <p className="listCell">{exercise.about}</p>
                <p className="listCell">
                  {getCategory(exercisesCategories, exercise.exercisesCategoriesId)}
                </p>
                <p className={`listCell ${exercise.statusId === 0 ? 'status_inactive' : 'status_active'}`}>
                  {exercise.statusId === 0 ? t("STATUS_INACTIVE") : t("STATUS_ACTIVE")}
                </p>
                <p className="listCell">
                  <ActionButtonTooltip
                    className="btn btn_type_delete"
                    onClick={() => handleDeleteConfirm(exercise)}
                    text={t("DELETE")}
                    tooltipPosition="left"
                  />
                </p>
              </li>
            ))}
          </ul>
        </article>
      ) : (
        <div className={styles.noExercises}>
          <p>{t("NO_USERS_ASSIGNED_TO_EXERCISE")}</p>
          <button 
            className="button hover"
            onClick={handleAttachExercises}
          >
            {t("ATTACH_EXERCISES")}
          </button>
        </div>
      )}

      {showAttachmentModal && (
        <ExerciseSelectionModal
          isOpen={showAttachmentModal}
          onClose={handleModalClose}
          exercises={allExercises}
          user={user}
          onSuccess={reloadUserExercises}
        />
      )}

      {exerciseToDelete && (
        <Modal onClose={handleDeleteCancel}>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            {/* <h3>{t("CONFIRM_DELETE")}</h3> */}
            <p>
              {t("ARE_YOU_SURE_YOU_WANT_TO_DELETE_THE_OPTION")} <strong>{exerciseToDelete.title}</strong>?
            </p>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                className="button hover"
                onClick={() => handleDeleteExercise(exerciseToDelete.id)}
                disabled={isDeleting}
              >
                {isDeleting ? t("LOADING_LABEL") + "..." : t("DELETE")}
              </button>
              <button
                className="button button_secondary hover"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                {t("CANCEL")}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </article>
  );
}

export default UserExercisesTable;